import { ContainerWrapper } from '../Container'
import { useEffect, useRef, useState } from 'react'
import SectionHeadingWrapper from './SectionHeadingWrapper'
import SectionHeading from './SectionHeading'
import QuoteIcon from '../../Icons/realEsatePostcards/quote.svg'
import QuoteIconWhite from '../../Icons/realEsatePostcards/quote-white.svg'

const Counter = ({
    to = 0,
    from = 0,
    duration = 2000,
    suffix = '',
    className = '',
}) => {
    const [value, setValue] = useState(from)
    const ref = useRef(null)
    const startedRef = useRef(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !startedRef.current) {
                        startedRef.current = true
                        const startTime = performance.now()
                        const animate = (now) => {
                            const elapsed = now - startTime
                            const progress = Math.min(elapsed / duration, 1)
                            const current = Math.floor(
                                from + (to - from) * progress,
                            )
                            setValue(current)
                            if (progress < 1) requestAnimationFrame(animate)
                        }
                        requestAnimationFrame(animate)
                    }
                })
            },
            { threshold: 0.2 },
        )

        observer.observe(el)
        return () => observer.disconnect()
    }, [to, from, duration])

    return (
        <span
            ref={ref}
            className={`elementor-counter-number ${className}`}
            data-duration={duration}
            data-to-value={to}
            data-from-value={from}>
            {value}
            {suffix}
        </span>
    )
}

const featuredTestimonialDefault =
    "Open Letter Marketing has been a game changer for my real estate business. Their high-quality mail pieces consistently get great response rates, and I've closed multiple deals thanks to their campaigns. Jake is always responsive, knowledgeable, and willing to help with any questions. Highly recommend to anyone looking to scale their marketing!"

const defaultTestimonials = [
    "I can't say enough good things about Open Letter Marketing. Their products are great and always improving. Their support is second to none. The people are amazing and always willing to help. I have used this company for my direct mail needs for a long time and do not plan on moving elsewhere.",
    "I've uploaded my lists, used OLM postcards, and I'm already seeing responses. The designs are powerful for investors.",
]

const defaultStats = [
    {
        label: 'Trusted by Investors',
        to: 1000,
        suffix: '+',
    },
    { label: 'ROI Generated for <br> Clients', to: 300, suffix: '%' },
    { label: 'Recognized by <br> Inc.', to: 5000, suffix: '' },
]

export default function InvestorsSay({
    heading = 'What Investors Say After Switching',
    featuredTestimonial = featuredTestimonialDefault,
    testimonials = defaultTestimonials,
    stats = defaultStats,
}) {
    return (
        <section aria-labelledby="investors-say-heading" className="py-5">
            <ContainerWrapper>
                <div className="px-4">
                    <SectionHeadingWrapper>
                        <SectionHeading>{heading}</SectionHeading>
                    </SectionHeadingWrapper>

                    <div className="grid grid-cols-1 lg:grid-cols-10 lg:grid-rows-1 gap-5 md:gap-5 max-w-[1200px] mx-auto">
                        {/* Left gradient promo card (spans two rows on desktop) */}
                        <div className="olm-gradient text-white rounded-3xl p-8 md:px-7 md:p-12 col-span-12 row-span-1 lg:col-span-3 lg:col-start-1 lg:row-span-2 flex flex-col justify-between min-h-[360px] shadow-[0_12px_24px_rgba(255,101,47,0.18)]">
                            <div className="flex flex-col items-center justify-center h-full text-center">
                                <div className="flex justify-center mb-4 md:mb-4">
                                    <QuoteIconWhite className="w-12 h-12" />
                                </div>
                                <blockquote className="text-white text-lg md:text-lg leading-relaxed">
                                    {featuredTestimonial}
                                </blockquote>
                            </div>
                        </div>

                        {/* Center: Testimonials - top */}
                        <div className="col-span-12 lg:col-span-5 lg:col-start-4 lg:row-start-1">
                            <figure className="bg-white rounded-2xl border border-orange-200 px-5 py-7 md:px-5 text-center shadow-[0_4px_10px_0_rgba(237,92,47,0.5)]">
                                <div className="flex justify-center mb-3 md:mb-4">
                                    <QuoteIcon className="w-12 h-12 text-secondary" />
                                </div>
                                <blockquote className="text-black text-lg">
                                    {testimonials[0]}
                                </blockquote>
                            </figure>
                        </div>

                        {/* Right: Tall Stats */}
                        <div className="olm-gradient text-white rounded-3xl p-8 md:px-5 md:p-5 col-span-12 lg:col-span-2 lg:col-start-9 lg:row-span-2 flex flex-col justify-between min-h-[360px] shadow-[0_12px_24px_rgba(255,101,47,0.18)] items-center">
                            <div className="space-y-8">
                                {stats.map((s, idx) => (
                                    <div key={idx}>
                                        <div
                                            className="text-white/90 mb-2 md:mb-3 text-base md:text-lg font-medium"
                                            dangerouslySetInnerHTML={{
                                                __html: s.label,
                                            }}
                                        />
                                        <div className="text-white text-5xl md:text-6xl font-semibold leading-none">
                                            <Counter
                                                to={s.to}
                                                from={0}
                                                duration={2000}
                                                suffix={s.suffix}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Center: Testimonials - bottom */}
                        <div className="col-span-12 lg:col-span-5 lg:col-start-4 lg:row-start-2">
                            <figure className="bg-white rounded-2xl border border-orange-200 px-5 py-7 md:px-5 text-center shadow-[0_4px_10px_0_rgba(237,92,47,0.5)]">
                                <div className="flex justify-center mb-3 md:mb-4">
                                    <QuoteIcon className="w-12 h-12 text-secondary" />
                                </div>
                                <blockquote className="text-black text-lg">
                                    {testimonials[1]}
                                </blockquote>
                            </figure>
                        </div>
                    </div>
                </div>
            </ContainerWrapper>
        </section>
    )
}
